import { Body1Strong } from '@fluentui/react-components';

import {
  ChangeNoteRow,
  DateLabel,
  DRAFT_VERSION_VALUE,
  NavOverviewLayout,
  TOverflowButtonParam,
  VersionLinkRow,
} from '@shared/components';
import { useSearchParams } from '@shared/hooks';
import { UserLabel } from '@modules/users';

import { isTLiteratureVersion, TLiterature, TLiteratureDraft, TLiteratureVersion } from '../types';
import { getTypeLabel } from '../helpers';

type TProps = {
  literature: TLiterature;
  version: TLiteratureDraft | TLiteratureVersion | undefined;
  actions: TOverflowButtonParam[];
};

const LiteratureNavOverview = ({ literature, version, actions }: TProps) => {
  const { change, searchParams } = useSearchParams();
  const view = searchParams.get('view');

  const content = version?.content;
  const versionIsVersion = isTLiteratureVersion(version);
  const isDraft = !!version && !versionIsVersion;
  const latestVersion = literature.versions[0];

  const selectedVersion = version ? (versionIsVersion ? version.id : DRAFT_VERSION_VALUE) : undefined;
  const authors: string[] = versionIsVersion ? version.authors : (literature.stats.authors ?? []);

  const showVersion = (next: string | undefined) => {
    if (next && next !== view) change('view', next);
  };

  return (
    <NavOverviewLayout
      actions={actions}
      versionDropdownProps={{
        versions: literature.versions,
        hasDraft: !!literature.draft,
        selectedVersion,
        onOptionSelect: (_, data) => showVersion(data.selectedOptions[0]),
      }}
      itemRows={
        <VersionLinkRow
          label="Latest version"
          versionId={latestVersion?.id}
          isCurrent={versionIsVersion && version.id === latestVersion?.id}
          getParams={(id) => ({ view: id })}
        />
      }
    >
      {content && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Type:</span>
          <Body1Strong>{getTypeLabel(content.type)}</Body1Strong>
        </p>
      )}

      {isDraft && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Modified:</span>
          <Body1Strong>
            <DateLabel>{literature.stats.modifiedDate}</DateLabel>
          </Body1Strong>
        </p>
      )}

      {versionIsVersion && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Completed:</span>
          <Body1Strong>
            <DateLabel>{version.completedDate}</DateLabel>
          </Body1Strong>
        </p>
      )}

      {authors.length > 0 && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Authors:</span>
          <Body1Strong>
            <span className="flex flex-col space-y-3 force-break-word">
              {authors.map((userId) => (
                <UserLabel id={userId} key={userId} />
              ))}
            </span>
          </Body1Strong>
        </p>
      )}

      {versionIsVersion && <ChangeNoteRow note={version.note} />}
    </NavOverviewLayout>
  );
};

export default LiteratureNavOverview;
